<template>
  <v-layout class="grey lighten-4" column>
    <div class="noto bold pa-3 d-md-flex align-center" width="100%">
      <div class="title primary--text">{{ section }}</div>
      <v-spacer />
      <div class="d-flex justify-end align-center">
        <v-text-field
          v-model="search"
          class="mr-3"
          append-icon="mdi-magnify"
          label="Buscar..."
          solo
          rounded
          hide-details
          flat
          dense/>
      </div>
      <v-tooltip left>
        <template
          v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            :href="exportRoute"
            target="_blank"
            dark
            fab
            x-small
            depressed
            color="secondary">
            <v-icon color="white">mdi-application-export</v-icon>
          </v-btn>
        </template>
        <span>Exportar balance a CSV</span>
      </v-tooltip>
    </div>

    <v-flex xs12>
      <v-data-table
        :loading-text="table.title"
        :server-items-length="table.total"
        :loading="table.loading"
        :headers="table.headers"
        :items="salesList.models"
        :options="table.options"
        :footer-props="{
          'items-per-page-options': [10, 20, 30, 40, 50]
        }"
        sort-by="name"
        @update:options="updateTable"
        class="elevation-1 fixed-header">
        <template v-slot:top>
          <div class="px-3 filters">
            <v-row>
              <v-col cols="3">
                <div class="d-flex align-center">
                  <span class="mr-3">
                    Filtros:
                  </span>
                  <v-menu
                    ref="datePicker"
                    v-model="datePicker"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="range"
                        label="Rango de fechas"
                        prepend-icon="mdi-calendar"
                        readonly
                        hide-details
                        dense
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="dates"
                      :range="true"
                      first-day-of-week="1"
                      no-title
                    >
                      <v-btn text color="primary" @click="resetDate">Borrar</v-btn>
                      <v-spacer/>
                      <v-btn text color="grey" @click="datePicker = false">Cancel</v-btn>
                      <v-btn :disabled="dates.length < 2" color="primary" @click="filterByDate">OK</v-btn>
                    </v-date-picker>
                  </v-menu>
                </div>
              </v-col>
            </v-row>
          </div>
        </template>
        <template v-slot:item.tip="{ item }">
          {{ item.tip | toFixNumber(2) | prefix('$') }}
        </template>
        <template v-slot:item.feeDetails="{ item }">
          {{ item.feeDetails | toFixNumber(2) | prefix('$') }}
        </template>
        <template v-slot:item.createdAt="{ item }">
          {{ convertToDateString(item.createdAt) }}
        </template>
        <template v-slot:item.total="{ item }">
          {{ item.total | toFixNumber(2) | prefix('$') }}
        </template>
      </v-data-table>
    </v-flex>

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-layout>
</template>

<script>
import Sale from '@/models/Sale'
import SalesList from '@/models/collections/SalesList'
import { cloneDeep } from 'lodash'

export default {
  name: 'Default',
  data () {
    let salesList = new SalesList()
    let sale = new Sale({}, salesList)

    return {
      sale,
      salesList,
      datePicker: false,
      dates: [],
      overlay: false,
      section: this.$i18n.t('sales.section'),
      table: {
        title: this.$i18n.t('sales.loading'),
        loading: true,
        headers: [
          { text: this.$i18n.t('sales.headers.order'), value: 'order' },
          { text: this.$i18n.t('sales.headers.tip'), value: 'tip', align: 'right' },
          { text: this.$i18n.t('sales.headers.fee'), value: 'feeDetails', align: 'right' },
          { text: this.$i18n.t('sales.headers.created'), value: 'createdAt', align: 'right' },
          { text: this.$i18n.t('sales.headers.total'), value: 'total', align: 'right' }
        ],
        data: [],
        total: 0,
        options: {
          groupBy: [],
          groupDesc: [],
          itemsPerPage: 50,
          multiSort: false,
          mustSort: false,
          page: 1,
          sortBy: [''],
          sortDesc: []
        }
      },
      search: ''
    }
  },
  computed: {
    range () {
      return this.dates.length < 2 ? '' : `${this.dates[0]} / ${this.dates[1]}`
    },
    exportRoute () {
      return `${process.env.VUE_APP_API_URL}/v1/sales/?output=file&access_token=${this.$store.state.login.token}&restaurant=${this.$store.state.currentRestaurant.data._id}&createdAt>=${this.dates[0] || ''}&createdAt<=${this.dates[1] || ''}`
    }
  },
  watch: {
    'search': async function (val) {
      const dates = {}
      if (this.dates.length) {
        dates['createdAt>'] = this.dates[0]
        dates['createdAt<'] = this.dates[1]
      }
      const params = {
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.options.page - 1)),
        sort: 'updatedAt',
        restaurant: this.$store.state.currentRestaurant.data._id,
        ...dates
      }
      if (val.length >= 3) {
        try {
          params.q = `/${val}/i`
          await this.load(params)
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
      } else {
        this.load(params)
      }
    }
  },
  methods: {
    async resetDate () {
      this.dates = []
      await this.load()
      this.datePicker = false
    },
    async filterByDate () {
      this.dates.sort((a, b) => {
        if (a < b) return -1
        if (a > b) return 1
        return 0
      })
      const dates = {}
      if (this.dates.length) {
        dates['createdAt>'] = this.dates[0]
        dates['createdAt<'] = this.dates[1]
      }
      const params = {
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.options.page - 1)),
        sort: 'updatedAt',
        restaurant: this.$store.state.currentRestaurant.data._id,
        ...dates
      }
      await this.load(params)
      this.datePicker = false
    },
    updateTable: async function (data) {
      const { sortBy, sortDesc } = data
      this.table.options = data
      if (this.$store.state.area) {
        const dates = {}
        if (this.dates.length) {
          dates['createdAt>'] = this.dates[0]
          dates['createdAt<'] = this.dates[1]
        }
        await this.load({
          limit: data.itemsPerPage,
          skip: (data.itemsPerPage * (data.page - 1)),
          sort: `${sortDesc[0] ? '-' : ''}${sortBy[0]}`,
          restaurant: this.$store.state.currentRestaurant.data._id,
          ...dates
        })
      }
    },
    async load (params = null) {
      params = params || {
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.options.page - 1)),
        sort: `${this.table.options.sortDesc[0] ? '-' : ''}${this.table.options.sortBy[0]}`,
        restaurant: this.$store.state.currentRestaurant.data._id
      }
      this.table.loading = true
      try {
        const { response } = await this.sale.fetch({ params })
        this.salesList.replace([response.data.data])
        this.table.total = response.data.total
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.table.loading = false
    },
    convertToDateString (date) {
      const d = new Date(date)
      return d.toLocaleDateString()
    },
    async exportCSV () {
      const { response } = await this.sale.fetch()
      const { data } = cloneDeep(response.data)
      const headers = ['id', 'order', 'total', 'tip', 'commision'].join(',')
      const body = data.map(d => [
        d.id,
        d.order,
        d.total,
        d.tip,
        d.feeDetails
      ].join(',')).join('\n')
      const csv = headers + '\n' + body
      const encodedUri = encodeURI(`data:text/csv;charset=utf-8,${csv}`)
      window.open(encodedUri)
    }
  }
}
</script>
